import type {
  ShareDimension as Dimension,
  ShareDocument,
} from "./share-document";
import { parseShareDocument } from "./share-document";
import { decodeSharePayload, encodeSharePayload } from "./payload-transport";
import {
  decodeBoundedArray,
  decodeTuple,
  migrateVersion1,
  migrateVersion2,
} from "./migrations";

const CURRENT_SHARE_PAYLOAD_VERSION = 3;

type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

type WireFlag = 0 | 1;
type PlaybackTuple = [0, WireFlag] | [1, WireFlag, number];
type CurrentShareTuple = [
  typeof CURRENT_SHARE_PAYLOAD_VERSION,
  Dimension,
  WireFlag,
  WireFlag,
  PlaybackTuple,
  JsonValue,
  JsonValue,
  JsonValue,
  JsonValue,
];

export async function encodeShareDocument(
  share: ShareDocument,
): Promise<string> {
  return encodeSharePayload(encodeCurrentShareDocument(share));
}

export async function decodeShareDocument(
  fragment: string,
): Promise<ShareDocument> {
  return decodeShareDocumentPayload(await decodeSharePayload(fragment));
}

export function decodeShareDocumentPayload(value: unknown): ShareDocument {
  if (!Array.isArray(value)) throw new Error("Invalid share payload shape");
  const version: unknown = value[0];
  let current: unknown;
  switch (version) {
    case 1:
      current = [CURRENT_SHARE_PAYLOAD_VERSION, ...migrateVersion1(value)];
      break;
    case 2:
      current = [CURRENT_SHARE_PAYLOAD_VERSION, ...migrateVersion2(value)];
      break;
    case CURRENT_SHARE_PAYLOAD_VERSION:
      current = value;
      break;
    default:
      throw new Error("Unsupported share payload");
  }
  return parseShareDocument(decodeCurrentTuple(current));
}

function encodeCurrentShareDocument(share: ShareDocument): CurrentShareTuple {
  const equation = (dimension: Dimension): JsonValue => {
    const equationSnapshot = share.equations[dimension];
    return [
      equationSnapshot.matrices.map(encodeMatrix),
      equationSnapshot.vectors.map(encodeVector),
      [...equationSnapshot.appliedTransform],
    ];
  };
  const camera = (dimension: Dimension): JsonValue => {
    const cameraSnapshot = share.cameras[dimension];
    return [
      [...cameraSnapshot.position],
      [...cameraSnapshot.target],
      cameraSnapshot.zoom,
    ];
  };
  const modeFlag: WireFlag = share.playback.mode === "composed" ? 1 : 0;
  const playback: PlaybackTuple =
    share.playback.status === "idle"
      ? [0, modeFlag]
      : [1, modeFlag, share.playback.elapsedMs];
  return [
    CURRENT_SHARE_PAYLOAD_VERSION,
    share.activeDimension,
    share.showBasis ? 1 : 0,
    share.showGrid ? 1 : 0,
    playback,
    equation(2),
    equation(3),
    camera(2),
    camera(3),
  ];
}

function encodeMatrix(matrix: {
  readonly label: string;
  readonly entries: readonly string[];
  readonly durationMs: number;
}): JsonValue {
  return [matrix.label, [...matrix.entries], matrix.durationMs];
}

function encodeVector(vector: {
  readonly label: string;
  readonly coordinates: readonly string[];
  readonly color: string;
}): JsonValue {
  return [vector.label, [...vector.coordinates], vector.color];
}

function decodeCurrentTuple(value: unknown): unknown {
  const [
    version,
    activeDimension,
    showBasis,
    showGrid,
    playback,
    equation2,
    equation3,
    camera2,
    camera3,
  ] = decodeTuple(value, 9);
  void version;
  return {
    activeDimension: decodeDimension(activeDimension),
    showBasis: decodeFlag(showBasis),
    showGrid: decodeFlag(showGrid),
    playback: decodePlaybackTuple(playback),
    equations: {
      2: decodeEquationTuple(equation2, 2),
      3: decodeEquationTuple(equation3, 3),
    },
    cameras: {
      2: decodeCameraTuple(camera2),
      3: decodeCameraTuple(camera3),
    },
  };
}

function decodePlaybackTuple(value: unknown): unknown {
  if (!Array.isArray(value)) throw new Error("Invalid share payload shape");
  const status = decodeFlag(value[0]);
  if (!status) {
    const [, mode] = decodeTuple(value, 2);
    return {
      status: "idle",
      mode: decodeFlag(mode) ? "composed" : "steps",
    };
  }
  const [, mode, elapsedMs] = decodeTuple(value, 3);
  return {
    status: "paused",
    mode: decodeFlag(mode) ? "composed" : "steps",
    elapsedMs,
  };
}

function decodeEquationTuple(value: unknown, dimension: Dimension): unknown {
  const [matrices, vectors, appliedTransform] = decodeTuple(value, 3);
  return {
    matrices: decodeBoundedArray(matrices).map((entry) => {
      const [label, entries, durationMs] = decodeTuple(entry, 3);
      return {
        label,
        entries: decodeTuple(entries, dimension ** 2),
        durationMs,
      };
    }),
    vectors: decodeBoundedArray(vectors).map((entry) => {
      const [label, coordinates, color] = decodeTuple(entry, 3);
      return {
        label,
        coordinates: decodeTuple(coordinates, dimension),
        color,
      };
    }),
    appliedTransform: decodeTuple(appliedTransform, dimension ** 2),
  };
}

function decodeCameraTuple(value: unknown): unknown {
  const [position, target, zoom] = decodeTuple(value, 3);
  return {
    position: decodeTuple(position, 3),
    target: decodeTuple(target, 3),
    zoom,
  };
}

function decodeDimension(value: unknown): Dimension {
  if (value !== 2 && value !== 3) throw new Error("Invalid dimension");
  return value;
}

function decodeFlag(value: unknown): boolean {
  if (value !== 0 && value !== 1) throw new Error("Invalid flag");
  return value === 1;
}
